import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  apiMessage,
  listNotificationRuns,
  listNotificationSettings,
  saveNotificationSettings,
} from '../services/jobAuditApi';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const fieldClass =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm shadow-sm';

const JobAuditNotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState('');
  const [draft, setDraft] = useState(null);
  const [runs, setRuns] = useState([]);

  const load = () => {
    setLoading(true);
    return listNotificationSettings()
      .then((payload) => {
        const next = payload.items || [];
        setItems(next);
        setSelected((current) => current || next[0]?.tenant_code || '');
      })
      .catch((err) => {
        toast.error(apiMessage(err, 'Failed to load notification settings'));
        setItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const current = items.find((item) => item.tenant_code === selected);
    setDraft(current ? { ...current } : null);
    if (!selected) {
      setRuns([]);
      return undefined;
    }
    let cancelled = false;
    listNotificationRuns(selected)
      .then((payload) => {
        if (!cancelled) setRuns(payload.items || []);
      })
      .catch(() => {
        if (!cancelled) setRuns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, items]);

  const save = () => {
    if (!draft) return;
    setSaving(true);
    saveNotificationSettings(draft.tenant_code, {
      enabled: Boolean(draft.enabled),
      cadence: 'weekly',
      day_of_week: Number(draft.day_of_week),
      local_send_time: draft.local_send_time,
      minimum_30d_audits: Number(draft.minimum_30d_audits),
    })
      .then((saved) => {
        toast.success(saved.enabled ? 'Automatic emails enabled' : 'Settings saved');
        return load();
      })
      .catch((err) => toast.error(apiMessage(err, 'Could not save notification settings')))
      .finally(() => setSaving(false));
  };

  const last = runs[0];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Technician notifications</h1>
        <p className="mt-1 text-sm text-gray-600">
          Weekly progress emails stay off until an administrator enables them for a tenant.
          Enabling takes effect on the next scheduled run.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-gray-600">Loading tenants…</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
          <div className="rounded-lg bg-white p-3 shadow">
            {items.map((item) => (
              <button
                key={item.tenant_code}
                type="button"
                className={`block w-full rounded px-2 py-2 text-left text-sm ${
                  item.tenant_code === selected ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelected(item.tenant_code)}
              >
                <div className="font-medium">{item.tenant_name || item.tenant_code}</div>
                <div className="text-xs text-gray-500">
                  {item.tenant_code} · {item.enabled ? 'On' : 'Off'}
                </div>
              </button>
            ))}
          </div>
          {draft ? (
            <div className="space-y-4 rounded-lg bg-white p-4 shadow">
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={Boolean(draft.enabled)}
                  onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
                />
                Send automatic weekly progress emails
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm text-gray-700">
                  Day
                  <select
                    className={fieldClass}
                    value={draft.day_of_week}
                    onChange={(event) =>
                      setDraft({ ...draft, day_of_week: Number(event.target.value) })
                    }
                  >
                    {DAYS.map((label, index) => (
                      <option key={label} value={index}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-gray-700">
                  Local send time
                  <input
                    className={fieldClass}
                    value={draft.local_send_time || '08:00'}
                    onChange={(event) =>
                      setDraft({ ...draft, local_send_time: event.target.value })
                    }
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Minimum audits in 30 days
                  <input
                    type="number"
                    min="1"
                    className={fieldClass}
                    value={draft.minimum_30d_audits}
                    onChange={(event) =>
                      setDraft({ ...draft, minimum_30d_audits: event.target.value })
                    }
                  />
                </label>
                <div className="text-sm text-gray-700">
                  Timezone
                  <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
                    {draft.timezone || 'Not configured'}
                  </div>
                </div>
              </div>
              {!draft.timezone_configured ? (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                  Set this tenant&apos;s timezone before enabling automatic emails.
                </p>
              ) : null}
              <p className="text-sm text-gray-600">
                Next run: {draft.next_run_at || '—'} · Technicians with enough audits:{' '}
                {draft.technicians_with_audits ?? 0}
              </p>
              <button
                type="button"
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
                onClick={save}
                disabled={saving || (draft.enabled && !draft.timezone_configured)}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <div className="border-t pt-3 text-sm text-gray-700">
                <div className="font-medium text-gray-900">Last run</div>
                {last ? (
                  <p className="mt-1">
                    {last.status} · sent {last.sent_count} · skipped {last.skipped_count} · failed{' '}
                    {last.failed_count}
                  </p>
                ) : (
                  <p className="mt-1 text-gray-500">No automatic runs yet.</p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default JobAuditNotificationSettings;
